// src/pages/DemoPage.js
import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  Play, Pause, Calendar, ChevronDown, Maximize2
} from 'lucide-react';
import { pageVariants, pageTransition } from '../utils/pageTransitions';

const DemoPage = () => {
  const navigate = useNavigate();
  const [isPlaying, setIsPlaying] = useState(false);
  const [hasPlayed, setHasPlayed] = useState(false);
  const [activeScenario, setActiveScenario] = useState(null);
  const [currentTime, setCurrentTime] = useState(0);
  const videoRef = useRef(null);
  const [videoDimensions, setVideoDimensions] = useState({ width: 420, height: 773 });

  const scenarios = [
    { name: 'Late Night Emergencies Support', time: 0 },
    { name: 'Application Requirements Inquiry', time: 44 },
    { name: 'Multilingual Voice Chat', time: 115 }
  ];

  // Calculate video dimensions based on viewport
  const calculateVideoDimensions = () => {
    const viewportHeight = window.innerHeight;
    const availableHeight = viewportHeight - 200; // Space for nav, title, padding
    const maxHeight = 773;
    const maxWidth = 420;
    const aspectRatio = maxWidth / maxHeight;

    if (availableHeight < maxHeight) {
      // Viewport is constrained, scale down proportionally
      const newHeight = availableHeight;
      const newWidth = newHeight * aspectRatio;
      setVideoDimensions({ width: newWidth, height: newHeight });
    } else {
      // Enough space, use full size
      setVideoDimensions({ width: maxWidth, height: maxHeight });
    }
  };

  // Update active scenario based on current video time
  React.useEffect(() => {
    const handleTimeUpdate = () => {
      if (videoRef.current) {
        const time = videoRef.current.currentTime;
        setCurrentTime(time);
        
        // Determine which scenario is active based on current time
        let active = null;
        for (let i = scenarios.length - 1; i >= 0; i--) {
          if (time >= scenarios[i].time) {
            active = i;
            break;
          }
        }
        setActiveScenario(active);
      }
    };

    const video = videoRef.current;
    if (video) {
      video.addEventListener('timeupdate', handleTimeUpdate);
      return () => video.removeEventListener('timeupdate', handleTimeUpdate);
    }
  }, []);

  const jumpToScenario = (scenarioIndex) => {
    if (videoRef.current) {
      videoRef.current.currentTime = scenarios[scenarioIndex].time;
      if (!isPlaying) {
        videoRef.current.play();
        setIsPlaying(true);
        setHasPlayed(true);
      }
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Handle viewport resize
  React.useEffect(() => {
    calculateVideoDimensions();
    
    const handleResize = () => {
      calculateVideoDimensions();
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
        setHasPlayed(true);
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleVideoClick = () => {
    togglePlay();
  };

  return (
    <motion.div
      initial="initial"
      animate="in"
      exit="out"
      variants={pageVariants}
      transition={pageTransition}
      className="min-h-screen pt-20"
    >
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="text-center mb-16"
            >
              <h1 className="text-4xl font-bold text-gray-900 mb-4">
                Experience the Difference
              </h1>
              <p className="text-xl text-gray-600">
                See how our AI assistant can transform your resident services
              </p>
            </motion.div>

            {/* Video Demo Section */}
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="mb-16"
            >
              <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-3xl p-4 sm:p-8 lg:p-12">
                <h3 className="text-3xl font-bold mb-8 text-center">
                  See It In Action
                </h3>
                
                <div 
                  className="relative rounded-2xl overflow-hidden shadow-2xl bg-black mx-auto w-full" 
                  style={{ 
                    maxWidth: `${videoDimensions.width}px`, 
                    maxHeight: `${videoDimensions.height}px` 
                  }}
                >
                  <video
                    ref={videoRef}
                    className="w-full h-auto"
                    style={{ 
                      maxWidth: `${videoDimensions.width}px`, 
                      maxHeight: `${videoDimensions.height}px` 
                    }}
                    src="/demo.mp4"
                    onClick={handleVideoClick}
                    onPlay={() => setIsPlaying(true)}
                    onPause={() => setIsPlaying(false)}
                    playsInline
                  >
                    Your browser does not support the video tag.
                  </video>
                  
                  {/* Custom Play Button Overlay (shows when video is paused) */}
                  {!isPlaying && (
                    <div 
                      className={`absolute inset-0 flex items-center justify-center cursor-pointer transition-opacity ${hasPlayed ? 'bg-black/20' : ''}`}
                      onClick={handleVideoClick}
                    >
                      <button className="bg-white/90 backdrop-blur text-gray-900 px-8 py-4 rounded-full font-semibold hover:bg-white transition-all flex items-center gap-3 shadow-xl">
                        <Play className="h-6 w-6" fill="currentColor" />
                        <span>Watch Demo</span>
                      </button>
                    </div>
                  )}
                </div>

                <div className="mt-8 text-center">
                  {/* Scenario Chapter Buttons */}
                  <div className="mb-6">
                    <p className="text-sm text-gray-500 mb-2">Jump to scenario:</p>
                    <div className="flex flex-wrap justify-center gap-2">
                      {scenarios.map((scenario, index) => (
                        <button
                          key={index}
                          onClick={() => jumpToScenario(index)}
                          className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                            activeScenario === index
                              ? 'bg-blue-600 text-white shadow-lg scale-105'
                              : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                          }`}
                        >
                          <span className="hidden sm:inline">{scenario.name}</span>
                          <span className="sm:hidden">{scenario.name.split(' ').slice(0, 2).join(' ')}</span>
                          <span className="ml-2 text-xs">
                            {formatTime(scenario.time)}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>

                  <p className="text-gray-600 mb-4">
                    Watch how residents interact with our AI assistant to get instant answers 
                    about their housing questions in multiple languages.
                  </p>
                  
                  <div className="flex flex-wrap justify-center gap-4 text-sm text-gray-500">
                    <span className="flex items-center gap-1">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      Real resident scenarios
                    </span>
                    <span className="flex items-center gap-1">
                      <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                      Multiple languages
                    </span>
                    <span className="flex items-center gap-1">
                      <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                      Instant responses
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Onboarding Timeline */}
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="mb-16 bg-gray-50 rounded-3xl p-8"
            >
              <h3 className="text-2xl font-bold mb-8 text-center">Simple Onboarding</h3>
              <div className="flex justify-center">
                <div className="flex flex-col md:flex-row justify-between md:items-center gap-8 md:gap-4 md:w-full">
                  {['Kick-off', 'Document Ingest', 'Theming', 'Pilot', 'Go-Live'].map((step, i) => (
                    <div key={i} className="flex flex-col md:flex-row items-start md:items-center">
                      <div className="relative">
                        <div className="flex items-center gap-4 w-full md:w-auto">
                          <div className="w-12 h-12 bg-blue-600 text-white rounded-full flex items-center justify-center font-bold flex-shrink-0 relative z-10">
                            {i + 1}
                          </div>
                          <span className="font-medium">{step}</span>
                        </div>
                        {/* Vertical connector for mobile - extends from bottom of circle to top of next circle */}
                        {i < 4 && (
                          <div className="absolute left-6 top-12 h-20 w-0.5 bg-gray-300 md:hidden -translate-x-1/2"></div>
                        )}
                      </div>
                      {/* Horizontal arrow for desktop */}
                      {i < 4 && (
                        <ChevronDown className="h-6 w-6 text-gray-400 rotate-[-90deg] hidden md:block ml-4" />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>

            {/* CTA */}
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="text-center"
            >
              <h3 className="text-2xl font-bold mb-4">Ready to Get Started?</h3>
              <p className="text-gray-600 mb-8">
                Join housing authorities across the nation in providing cutting-edge service to residents
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button
                  onClick={() => navigate('/contact')}
                  className="bg-blue-600 text-white px-8 py-4 rounded-full font-semibold hover:bg-blue-700 transition-all flex items-center justify-center gap-2"
                >
                  <Calendar className="h-5 w-5" /> Book 30-min Call
                </button>
                <a
                  href="/ProductOnePager.pdf"
                  download="HousingAI-Product-One-Pager.pdf"
                  className="bg-gray-200 text-gray-800 px-8 py-4 rounded-full font-semibold hover:bg-gray-300 transition-all inline-block"
                >
                  Download Info Pack
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </motion.div>
  );
};

export default DemoPage;

// ===== END OF src/pages/DemoPage.js =====